import { getThemeClasses } from "../../../../utils/ThemeClasses";
import { useState, useEffect } from "react";
import type { TFunction } from "i18next";
import {
  ActiveUnitsChart,
  AltasBajasChart,
} from "../../../../components/dashboards/DailyHistoryCharts";

export { ActiveUnitsChart, AltasBajasChart };

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sublabel?: string;
  iconBgColor?: string;
  themeClasses: ReturnType<typeof getThemeClasses>;
}

export function StatCard({
  icon,
  label,
  value,
  sublabel,
  iconBgColor = "bg-blue-500",
  themeClasses,
}: StatCardProps) {
  const [displayValue, setDisplayValue] = useState<number | string>(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Animación de conteo cuando el valor cambia
  useEffect(() => {
    if (typeof value === "number" && value !== displayValue) {
      setIsAnimating(true);
      const duration = 1000;
      const steps = 30;
      const currentValue = typeof displayValue === "number" ? displayValue : 0;
      const increment = (value - currentValue) / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.round(currentValue + increment * currentStep);
        setDisplayValue(newValue);

        if (currentStep >= steps) {
          clearInterval(timer);
          setDisplayValue(value);
          setIsAnimating(false);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    } else if (typeof value === "string") {
      setDisplayValue(value);
    }
  }, [value, displayValue]);

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4 flex flex-col justify-between min-h-[100px] sm:min-h-[120px]`}
    >
      <div className="flex items-center justify-between">
        <div
          className={`w-8 h-8 ${iconBgColor} rounded-lg flex items-center justify-center`}
        >
          {icon}
        </div>
        <span className={`text-xs font-medium ${themeClasses.textSecondary}`}>
          {label}
        </span>
      </div>
      <div className="mt-auto">
        <p
          className={`text-lg sm:text-2xl font-bold ${themeClasses.text} ${
            isAnimating ? "transition-all duration-300 ease-out" : ""
          }`}
        >
          {displayValue}
        </p>
        {sublabel && (
          <p className={`text-xs ${themeClasses.textSecondary}`}>{sublabel}</p>
        )}
      </div>
    </div>
  );
}

interface StackedBarData {
  platform: string;
  totalUnits: number;
  healthy: { averagePercentage: number; companyCount: number; units: number };
  warning: { averagePercentage: number; companyCount: number; units: number };
  highRisk: { averagePercentage: number; companyCount: number; units: number };
  critical: { averagePercentage: number; companyCount: number; units: number };
}

interface StackedBarChartProps {
  data: StackedBarData[];
  themeClasses: ReturnType<typeof getThemeClasses>;
  t: TFunction<"translation", undefined>;
}

export function StackedBarChart({ data, themeClasses, t }: StackedBarChartProps) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setAnimated(false);
    const timer = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timer);
  }, [data]);

  // Usar el totalUnits más alto como 100% del eje Y
  const maxTotalUnits = Math.max(...data.map((d) => d.totalUnits || 0));

  // Redondear al múltiplo de 100 más cercano hacia arriba
  const roundedMax =
    maxTotalUnits > 0 ? Math.ceil(maxTotalUnits / 100) * 100 : 0;

  // Distribuir el eje Y en 8 niveles con números redondos (de abajo hacia arriba)
  const yAxisValues =
    roundedMax > 0
      ? [
          0,
          Math.round(roundedMax * 0.125),
          Math.round(roundedMax * 0.25),
          Math.round(roundedMax * 0.375),
          Math.round(roundedMax * 0.5),
          Math.round(roundedMax * 0.625),
          Math.round(roundedMax * 0.75),
          roundedMax,
        ]
      : [0, 5, 10, 15, 30, 50, 80, 100];

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}
    >
      {/* Legend and Filter */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-4 mb-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#22c55e] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.healthy")}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#e1d82e] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.warning")}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#f59e0b] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.highRisk")}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#ef4444] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.critical")}
            </span>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="flex">
        {/* Y Axis */}
        <div className="flex flex-col justify-between h-72 sm:h-64 pr-2 sm:pr-3 text-right">
          {[...yAxisValues].reverse().map((val, idx) => (
            <span
              key={`${val}-${idx}`}
              className={`text-xs ${themeClasses.text}`}
            >
              {val}
            </span>
          ))}
        </div>

        {/* Bars */}
        <div className="flex-1 flex justify-around items-end h-48 sm:h-64 border-l border-b border-gray-200 dark:border-gray-700">
          {data.map((item) => {
            // Ajustar scale al contenedor real (192px en móvil, 256px en desktop)
            const containerHeight = window.innerWidth >= 640 ? 256 : 192;
            const scale = containerHeight / roundedMax;

            // Usar averagePercentage de healthSummary para calcular alturas
            const healthyHeight =
              (((item.healthy?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;
            const warningHeight =
              (((item.warning?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;
            const highRiskHeight =
              (((item.highRisk?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;
            const criticalHeight =
              (((item.critical?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;

            return (
              <div
                key={item.platform}
                className="flex flex-col items-center flex-1 max-w-24"
              >
                <div className="flex flex-col-reverse w-full">
                  {healthyHeight > 0 && (
                    <div
                      className="w-full bg-[#22c55e]"
                      style={{
                        height: animated ? `${healthyHeight}px` : "0px",
                        transition: "height 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    ></div>
                  )}
                  {warningHeight > 0 && (
                    <div
                      className="w-full bg-[#e1d82e]"
                      style={{
                        height: animated ? `${warningHeight}px` : "0px",
                        transition:
                          "height 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.1s",
                      }}
                    ></div>
                  )}
                  {highRiskHeight > 0 && (
                    <div
                      className="w-full bg-[#f59e0b]"
                      style={{
                        height: animated ? `${highRiskHeight}px` : "0px",
                        transition:
                          "height 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.2s",
                      }}
                    ></div>
                  )}
                  {criticalHeight > 0 && (
                    <div
                      className="w-full bg-[#ef4444] rounded-t-sm"
                      style={{
                        height: animated ? `${criticalHeight}px` : "0px",
                        transition:
                          "height 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.3s",
                      }}
                    ></div>
                  )}
                </div>
                <span
                  className={`text-xs ${themeClasses.text} translate-y-5 -mt-4 drop-shadow-sm`}
                >
                  {item.platform}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
